<template>
  <div class="drag-enabled no-select">
    <div class="stage" v-if="editStage === stage.status">
      <VTextField
        v-model="stageEdited.name"
        autofocus
        solo
        @keypress.enter="updateStage"
        class="stage-field"
      />
      <div class="d-flex justify-end">
        <VBtn
          @click="updateStage"
          class="stage-btn mr-1"
          color="#1fc57f"
          width="1.5rem"
          height="1.5rem"
        >
          <VIcon>
            mdi-check
          </VIcon>
        </VBtn>
        <VBtn
          @click="clear"
          class="stage-btn"
          color="#1fc57f"
          width="1.5rem"
          height="1.5rem"
        >
          <VIcon>
            mdi-close
          </VIcon>
        </VBtn>
      </div>
    </div>

    <div class="stage-text drag-enabled editable d-flex align-center" v-else>
      <AppCheckbox
        v-show="operationsEnabled"
        :value="isGroupSelected"
        class="stage--checkbox"
        @input="select"
      />
      <VIcon v-if="!isShowed" style="font-size: 1.125rem; padding-right: 0.25rem">
        mdi-eye-off
      </VIcon>
      <span class="drag-enabled stage-text__text" @mouseup="edit">
        {{ `${stage.name}&nbsp;` }}
      </span>
      <span class="stage-text__count">({{ count }})</span>
    </div>
    <VMenu
      class="stage-menu"
      bottom
      left
    >
      <template v-slot:activator="{ on, attrs }">
        <VBtn
          icon
          v-bind="attrs"
          v-on="on"
          class="stage-menu-btn"
        >
          <VIcon color="#757575">
            mdi-dots-vertical
          </VIcon>
        </VBtn>
      </template>
      <template v-slot:activator="{ on, attrs }">
        <VBtn
          icon
          v-bind="attrs"
          v-on="on"
          class="stage-menu-btn"
        >
          <VIcon color="#757575">
            mdi-dots-vertical
          </VIcon>
        </VBtn>
      </template>

      <VList class="stage-list">
        <VListItem class="stage-list-item" v-if="!stage.system">
          <VList class="kanban-settings">
            <VListItem v-if="isShowed" class="kanban-settings-item">
              <a href="#" @click.prevent="hideStage">{{ $t('hide') }}</a>
            </VListItem>
            <VListItem v-else class="kanban-settings-item">
              <a href="#" @click.prevent="showStage">{{ $t('show') }}</a>
            </VListItem>
            <VListItem class="kanban-settings-item">
              <a href="#" @click.prevent="deleteStage">{{ $t('delete') }}</a>
            </VListItem>
          </VList>
        </VListItem>
        <VListItem v-else class="stage-list-item">
          <a href="#" v-if="isShowed" @click.prevent="hideStage">{{ $t('hide') }}</a>
          <a href="#" v-else @click.prevent="showStage">{{ $t('show') }}</a>
        </VListItem>
      </VList>
    </VMenu>
  </div>
</template>
<script>
import { mapActions } from 'vuex';
import AppCheckbox from '@/components/AppCheckbox';
import { useKanbanMultipleOperations } from '@/compositions/useKanbanMultipleOperations';

export default {
  name: 'VacancyCandidatesKanbanStage',
  components: { AppCheckbox },
  setup() {
    const { operationsEnabled } = useKanbanMultipleOperations();
    return {
      operationsEnabled,
    };
  },
  props: {
    editStage: {
      type: String,
      default: '',
    },
    isGroupSelected: Boolean,
    count: {
      type: Number,
      required: true,
    },
    stage: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      stageEdited: null,
      checked: this.isGroupSelected,
    };
  },
  computed: {
    isShowed() {
      return this.stage.active === null || this.stage.active;
    },
  },
  created() {
    this.stageEdited = { ...this.stage };
  },
  methods: {
    ...mapActions('kanban', ['update', 'delete', 'create', 'deleteTemp']),
    clear() {
      if (this.stageEdited.temp) {
        this.deleteTemp(this.stageEdited);
      }
      this.$emit('input', '');
    },
    edit() {
      if (this.stage.system) {
        return;
      }
      this.$emit('input', this.stage.status);
    },
    select(value) {
      this.$emit('select', value);
    },
    async updateStage() {
      if (this.stageEdited.temp) {
        await this.create(this.stageEdited);
      } else {
        await this.update(this.stageEdited);
      }
      this.clear();
    },
    async deleteStage() {
      this.$emit('delete', this.stageEdited);
    },
    async hideStage() {
      await this.update({ ...this.stageEdited, active: !this.stageEdited });
    },
    async showStage() {
      await this.update({ ...this.stageEdited, active: true });
    },
  },
};
</script>

<style lang="scss" scoped>
.no-select {
  user-select: none;
}
.editable:hover {
  cursor: pointer;
  background: #e0e0e0;
  padding: 0.1rem;
}
.stage {
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
}
.stage-text {
  position: relative;
  display: flex;
  overflow: hidden;
  height: 2.625rem;
  &:hover {
    padding: 0;
    background: transparent;
    color: #000;
  }

  &__text {
    margin-left: 20px;
    max-width: 90px;
    font-size: 12px;
    line-height: 12px;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    overflow: hidden;
    text-overflow: ellipsis;
    @media screen and (max-width: 768px) {
      max-width: 100%;
    }
  }
  &__count {
    margin-left: 5px;
  }
}

.stage-field.v-input {
  border: 2px solid #0a82d9;
  height: 42px;
  font-size: 0.8125rem;
  margin-bottom: 0.25rem;
  input {
    padding: 0.625rem 0.375rem;
  }
}

.stage-btn.v-btn:not(.v-btn--round).v-size--default {
  min-width: 1.5rem;
  .v-icon {
    font-size: 1rem;
  }
}
.stage-menu-btn {
  position: absolute;
  right: 0.25rem;
  top: 0.5625rem;
  border-radius: 0.25rem;
  height: 23px;
  width: 23px;
}
.stage-list {
  padding: 0.375rem 0;
  .stage-list-item {
    font-size: 0.875rem;
    min-height: 1.75rem;
    a {
      text-decoration: none;
    }
  }
}
</style>

<style lang="scss">
  .stage-field.v-text-field.v-text-field--solo:not(.v-text-field--solo-flat) > .v-input__control {
    min-height: 38px;
    > .v-input__slot {
      padding: 0;
      box-shadow: none;
      input {
        padding: 0.5rem 0.375rem;
        max-height: 38px;
        border: none;
        background: white;
        border-radius: 0.25rem;
      }
    }
  }

  .kanban-settings {
    padding: 0.375rem 0;
    .kanban-settings-item {
      font-size: 0.875rem;
      min-height: 1.75rem;
      a {
        text-decoration: none;
      }
    }
  }

  .stage--checkbox {
    position: absolute;
    cursor: pointer;
  }
</style>

<i18n locale="en" lang="yaml">
delete: Delete
hide: Hide
show: Show
edit: Rename
</i18n>

<i18n locale="ru" lang="yaml">
delete: Удалить
hide: Скрыть
show: Показать
edit: Переименовать
</i18n>
